import * as React from "react";
import {
  ColumnDef,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import clsx from "clsx";

export type RenderSubComponentProps<TData> = (props: {
  row: Row<TData>;
}) => React.ReactNode;

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  rowUrl?: (original: TData, row: Row<TData>) => string;
  rowOnClick?: (original: TData, row: Row<TData>) => void;
  renderSubComponent?: RenderSubComponentProps<TData>;
  pageSize?: number;
  currentPage?: number;
  isDifferentTableColumns?: boolean;
  useBackendSorting?: boolean;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  rowUrl,
  rowOnClick,
  renderSubComponent,
  pageSize = 10,
  currentPage = 1,
  isDifferentTableColumns = false,
  useBackendSorting = false,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getPaginationRowModel: getPaginationRowModel(),
    ...(useBackendSorting
    ? {}
    : { getSortedRowModel: getSortedRowModel() }),
    getRowCanExpand: () => true,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      pagination: {
        pageIndex: currentPage - 1,
        pageSize,
      },
    },
  });

  return (
    <div>
      <div className="border-b border-border">
        <Table>
          <TableHeader className="bg-background shadow-md rounded-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="px-4"
                      style={{
                        width:
                          header.column.id === "expender"
                            ? "5%"
                            : `${header.getSize()}px`,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => [
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={clsx(
                    `hover:bg-slate-300 dark:hover:bg-slate-700 ${
                      rowOnClick ? "cursor-pointer" : ""
                    }`
                  )}
                  onClick={() => rowOnClick && rowOnClick(row.original, row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-4 relative">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                      {rowUrl && (
                        <a
                          href={rowUrl(row.original, row)}
                          className="absolute top-0 left-0 h-full w-full z-1"
                        />
                      )}

                    </TableCell>
                  ))}
                </TableRow>,
                renderSubComponent &&
                  row.getIsExpanded() && (
                      isDifferentTableColumns ? (
                        <TableRow key={`${row.id}-expanded`}>
                          <TableCell colSpan={columns.length} className="p-0">
                            {renderSubComponent({ row })}
                          </TableCell>
                        </TableRow>
                      ) : (
                        <React.Fragment key={`${row.id}-render`}>
                          {renderSubComponent({ row })}
                        </React.Fragment>
                      )
                    
                  )
              ])
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
